import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Combined URL + upload input for admin cover images.
 * Uses /api/admin/upload/image (Emergent Object Store). Falls back to typing a URL.
 *
 * Props:
 *   value       current URL
 *   onChange    (nextUrl) => void
 *   placeholder input placeholder
 *   testid      base data-testid
 *   aspect      "16/10" | "4/3" | "1/1" — preview shape (default 16/10)
 */
export default function ImageUploader({ value, onChange, placeholder = "URL gambar…", testid, aspect = "16/10" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Format gambar harus JPG, PNG, atau WEBP.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Ukuran gambar melebihi 8 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(r.data.url);
      toast.success("Gambar terunggah");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload gambar");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const aspectClass = { "16/10": "aspect-[16/10]", "4/3": "aspect-[4/3]", "1/1": "aspect-square" }[aspect] || "aspect-[16/10]";

  return (
    <div className="space-y-2" data-testid={testid}>
      {/* Preview */}
      {value ? (
        <div className={"relative rounded-xl overflow-hidden border border-[#E8E4DB] bg-[#F3F1ED] " + aspectClass}>
          <img src={value} alt="preview" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange?.("")}
            data-testid={testid ? `${testid}-clear` : undefined}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 hover:bg-white text-[#BE5730] flex items-center justify-center shadow-sm"
            title="Hapus gambar">
            <X size={14} strokeWidth={2.25} />
          </button>
        </div>
      ) : (
        <button type="button" onClick={onPick}
          data-testid={testid ? `${testid}-pick-placeholder` : undefined}
          className={"w-full " + aspectClass + " rounded-xl border-2 border-dashed border-[#E8E4DB] bg-[#FAFAF8] hover:border-[#D2673E] hover:bg-[#FEFAF7] transition-colors flex flex-col items-center justify-center text-[#8A8F99]"}>
          <ImageIcon size={28} strokeWidth={1.5} />
          <span className="mt-2 text-xs font-medium">Klik untuk pilih gambar</span>
          <span className="text-[10px] text-[#8A8F99]">JPG · PNG · WEBP · max 8 MB</span>
        </button>
      )}
      {/* Actions: URL input + upload button */}
      <div className="flex items-center gap-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          data-testid={testid ? `${testid}-url` : undefined}
          className="flex-1 h-9 text-xs"
        />
        <button type="button" onClick={onPick} disabled={uploading}
          data-testid={testid ? `${testid}-upload-btn` : undefined}
          className="h-9 px-3 rounded-md border border-[#E8E4DB] bg-white hover:border-[#D2673E] hover:text-[#D2673E] transition-colors inline-flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap">
          <Upload size={13} />
          {uploading ? "Mengunggah…" : "Unggah"}
        </button>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden"
          data-testid={testid ? `${testid}-file-input` : undefined} />
      </div>
    </div>
  );
}
